import { Box } from '@mui/material';
import CustomNotes from 'components/shared/CustomNotes';
import CustomTab from 'components/shared/CustomTab/CustomTab';
import { CompleteOperationType, Operation, StartOperationType } from 'model/alerts.model';
import { SyntheticEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';

import { useDealNotes } from '../hooks/useDealNotes';
import PriceBookProducts from '../PriceBookProducts';
import Section from './Section';
import { mapDealToDetailFields } from './utils';

const Details = () => {
  const { id } = useParams();
  const deal = useAppSelector((state) => state.deals.deal);

  const [tabValue, setTabValue] = useState(0);
  const startOperation: StartOperationType = (operation: Operation, message: string) => {};
  const completeOperation: CompleteOperationType = (
    operation: Operation,
    message: string,
    isError?: boolean,
  ) => {};

  const {
    opportunityInfo,
    financialInfo,
    additionalInfo,
    attributionTracking,
    systemInfo,
    projectInformationFields,
    financialInformationDualRevenueFields,
  } = mapDealToDetailFields(deal, deal.pipelineType);

  const manager = useDealNotes(id!, startOperation, completeOperation);

  const tabs = [
    {
      id: 1,
      label: 'Opportunity Information',
      isHidden: tabValue !== 0,
      Component: <Section label='Opportunity Information' data={opportunityInfo} />,
    },
    {
      id: 2,
      label: 'Financial Information',
      isHidden: tabValue !== 1,
      Component: <Section label='Financial Information' data={financialInfo} />,
    },
    {
      id: 3,
      label: 'Attribution & Tracking',
      isHidden: tabValue !== 2,
      Component: <Section label='Attribution & Tracking' data={attributionTracking} />,
    },
    {
      id: 4,
      label: 'Additional Information',
      isHidden: tabValue !== 3,
      Component: <Section label='Additional Information' data={additionalInfo} />,
    },
    {
      id: 5,
      label: 'System Information',
      isHidden: tabValue !== 4,
      Component: <Section label='System Information' data={systemInfo} />,
    },
    {
      id: 6,
      label: 'Price Book & Products',
      isHidden: tabValue !== 5,
      Component: <PriceBookProducts products={deal.products} pricebookId={deal.pricebookId} />,
    },
    {
      id: 7,
      label: 'Notes',
      isHidden: tabValue !== 6,
      Component: (
        <Box sx={{ width: '60%', margin: '0 auto', height: '100%' }}>
          <CustomNotes manager={manager} />
        </Box>
      ),
    },
  ];

  const contentProductionTabs = [
    {
      id: 1,
      label: 'Opportunity Information',
      isHidden: tabValue !== 0,
      Component: <Section label='Opportunity Information' data={opportunityInfo} />,
    },
    {
      id: 2,
      label: 'Financial Information (Dual Revenue)',
      isHidden: tabValue !== 1,
      Component: (
        <Section
          label='Financial Information (Dual Revenue)'
          data={financialInformationDualRevenueFields}
        />
      ),
    },
    {
      id: 3,
      label: 'Financial Information',
      isHidden: tabValue !== 2,
      Component: <Section label='Financial' data={financialInfo} />,
    },
    {
      id: 4,
      label: 'Project Information',
      isHidden: tabValue !== 3,
      Component: <Section label='Project Information' data={projectInformationFields} />,
    },
    {
      id: 5,
      label: 'Attribution & Tracking',
      isHidden: tabValue !== 4,
      Component: <Section label='Attribution & Tracking' data={attributionTracking} />,
    },
    {
      id: 6,
      label: 'Additional Information',
      isHidden: tabValue !== 5,
      Component: <Section label='Additional Information' data={additionalInfo} />,
    },
    {
      id: 7,
      label: 'System Information',
      isHidden: tabValue !== 6,
      Component: <Section label='System Information' data={systemInfo} />,
    },
    {
      id: 8,
      label: 'Price Book & Products',
      isHidden: tabValue !== 7,
      Component: <PriceBookProducts products={deal.products} pricebookId={deal.pricebookId} />,
    },
    {
      id: 9,
      label: 'Notes',
      isHidden: tabValue !== 8,
      Component: (
        <Box sx={{ width: '60%', margin: '0 auto', height: '100%' }}>
          <CustomNotes manager={manager} />,
        </Box>
      ),
    },
  ];

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '24px',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <CustomTab
        tabs={deal.pipelineType === 'CONTENT_PRODUCTION' ? contentProductionTabs : tabs}
        handleChange={handleChange}
        value={tabValue}
      />
    </Box>
  );
};

export default Details;
